/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, September 18th 2022, 3:35:55 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { FormConfiguration, FieldDependency, FieldValidator, FIELD_TYPES_API_RESPONSE } from "lib/schemas";
import { addOrUpdateItem, removeItem } from "lib/utils";
import create from "zustand";

/** The Store */
interface IFormBuilderState {
  currentFormId: FormConfiguration["id"];
  forms: FormConfiguration[];
  fieldDependencies: FieldDependency[];
  fieldValidators: FieldValidator[];
  fieldTypes: FIELD_TYPES_API_RESPONSE;
  setCurrentForm: (form: FormConfiguration) => void;
  addForm: (newForm: FormConfiguration) => void;
  removeForm: (formId: FormConfiguration["id"]) => void;
  addFieldDependency: (newFieldDependency: FieldDependency) => void;
  removeFieldDependency: (fieldDependencyId: FieldDependency["id"]) => void;
  addFieldValidator: (newFieldValidator: FieldValidator) => void;
  removeFieldValidator: (fieldValidatorId: FieldValidator["id"]) => void;
  clearformBuilderState: () => void;
  setFieldTypes: (fieldTypes: FIELD_TYPES_API_RESPONSE) => void;
}

export const useFormBuilderStore = create<IFormBuilderState>((set) => {
  return {
    currentFormId: null,
    forms: [],
    fieldDependencies: [],
    fieldValidators: [],
    fieldTypes: {},
    setCurrentForm: (currentForm) =>
      set((state) => {
        if (!currentForm?.id) return state;
        return { currentFormId: currentForm.id, forms: addOrUpdateItem(state.forms, currentForm, "id") };
      }),
    addForm: (newForm) => set((state) => ({ forms: addOrUpdateItem(state.forms, newForm, "id") })),
    removeForm: (formId) => set((state) => state),
    clearformBuilderState: () => set((state) => ({ ...state, formStateList: [] })),
    addFieldDependency: (newFieldDependency) =>
      set((state) => ({ fieldDependencies: addOrUpdateItem(state.fieldDependencies, newFieldDependency, "id") })),
    removeFieldDependency: (fieldDependencyId) =>
      set((state) => ({ fieldDependencies: removeItem(state.fieldDependencies, fieldDependencyId, "id") })),
    addFieldValidator: (newFieldValidator) =>
      set((state) => ({ fieldValidators: addOrUpdateItem(state.fieldValidators, newFieldValidator, "id") })),
    removeFieldValidator: (fieldValidatorId) =>
      set((state) => ({ fieldValidators: removeItem(state.fieldValidators, fieldValidatorId, "id") })),
    setFieldTypes: (fieldTypes) => set((state) => ({ fieldTypes })),
  };
});

/** Selectors */
export const currentFormSelector = (state: IFormBuilderState) =>
  state.forms.find((form) => form.id === state.currentFormId);
